import { useEffect, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SideDrawer, {
  FieldLabel,
  dateInputToISO,
  fieldSx,
  primaryButtonSx,
  todayISODate,
} from "components/SideDrawer";
import { useToast } from "components/Toast";
import { DateField } from "components/FormPickers";
import {
  EXPENSE_TYPES,
  ExpenseType,
  addExpense,
  getApiError,
} from "services/finance";

const MAX_RECEIPT_BYTES = 5 * 1024 * 1024;
const RECEIPT_TYPES = ["image/jpeg", "image/png", "application/pdf"];

const emptyForm = {
  expenseType: "salaries" as ExpenseType,
  description: "",
  amount: "",
  date: todayISODate(),
  notes: "",
};

const AddExpenseDrawer = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [receipt, setReceipt] = useState<File | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, date: todayISODate() });
      setReceipt(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      addExpense(
        {
          expenseType: form.expenseType,
          description: form.description.trim(),
          amount: Number(form.amount),
          date: dateInputToISO(form.date),
          notes: form.notes.trim(),
        },
        receipt,
      ),
    onSuccess: (res) => {
      showToast(res?.message || "Expense added", "success");
      queryClient.invalidateQueries({ queryKey: ["finance"] });
      onClose();
    },
    onError: (error: any) =>
      showToast(getApiError(error, "Failed to add expense"), "error"),
  });

  const handleReceipt = (file?: File) => {
    if (!file) return setReceipt(null);
    if (!RECEIPT_TYPES.includes(file.type)) {
      showToast("Receipt must be a JPG, PNG or PDF", "error");
      return;
    }
    if (file.size > MAX_RECEIPT_BYTES) {
      showToast("Receipt must be 5MB or smaller", "error");
      return;
    }
    setReceipt(file);
  };

  const canSubmit =
    form.description.trim() && Number(form.amount) > 0 && form.date;

  const set = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <SideDrawer open={open} onClose={onClose} title="Add Expense">
      <Box display="flex" flexDirection="column" gap="16px">
        <Box>
          <FieldLabel>Expense Type</FieldLabel>
          <TextField
            select
            fullWidth
            size="small"
            sx={fieldSx}
            value={form.expenseType}
            onChange={(e) => set("expenseType", e.target.value)}
          >
            {EXPENSE_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <FieldLabel>Description</FieldLabel>
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            placeholder="Staff Salaries — August 2026 payroll"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Box>
        <Box>
          <FieldLabel>Amount</FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="number"
            sx={fieldSx}
            placeholder="1,800,000"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">₦</InputAdornment>,
            }}
          />
        </Box>
        <Box>
          <FieldLabel>Date</FieldLabel>
          <DateField
            value={form.date}
            onChange={(value) => set("date", value)}
            disableFuture
          />
        </Box>
        <Box>
          <FieldLabel>Notes</FieldLabel>
          <TextField
            fullWidth
            multiline
            rows={3}
            sx={fieldSx}
            placeholder="Add any notes about this expense"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Box>
        <Box>
          <FieldLabel>Receipt (optional)</FieldLabel>
          <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap">
            <Button
              component="label"
              variant="outlined"
              size="small"
              sx={{ textTransform: "none", color: "#0b1c12", borderColor: "#D0D5DD" }}
            >
              {receipt ? "Change file" : "Upload receipt"}
              <input
                hidden
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => {
                  handleReceipt(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </Button>
            {receipt && (
              <>
                <Typography fontSize={13} color="#475467" sx={{ wordBreak: "break-all" }}>
                  {receipt.name}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  sx={{ textTransform: "none" }}
                  onClick={() => setReceipt(null)}
                >
                  Remove
                </Button>
              </>
            )}
          </Box>
          <Typography fontSize={12} color="#98A2B3" mt="4px">
            JPG, PNG or PDF, max 5MB
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="contained"
          sx={{ ...primaryButtonSx, mt: "8px" }}
          disabled={!canSubmit || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Adding..." : "Add Expense"}
        </Button>
        <Button
          onClick={onClose}
          sx={{ color: "#101828", textDecoration: "underline", textTransform: "none" }}
        >
          Cancel
        </Button>
      </Box>
    </SideDrawer>
  );
};

export default AddExpenseDrawer;
